import React, { useEffect, useState } from 'react';
import { useCart } from './CartContext';
import { useAuth } from './AuthContext';
import { useApi } from './ApiContext'; // ➡️ Import api instance
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import './CheckoutPage.css';

// Payment method icons
import toyyip from './icon/toyyip.png';
import tng from './icon/tng.png';
import duitnow from './icon/duitnow.png';

const paymentOptions = [
    { id: 'FPX', name: 'Online Banking (FPX)', logo: toyyip, fee: 0.5 },
    { id: 'TNG', name: "Touch 'n Go", logo: tng, fee: 0.0, unavailable: true },
    { id: 'BANK', name: 'Duitnow Bank Transfer', logo: duitnow, fee: 0.0, unavailable: true },
];

const CheckoutPage = () => {
    // ➡️ Dapatkan 'api' daripada ApiContext
    const { api } = useApi();
    
    // Get cartItems and the new loading state from the context
    const { cartItems, loading: cartLoading } = useCart();
    const { isAuthenticated, loading: authLoading, username, email } = useAuth();
    const history = useHistory();

    const [selectedPayment, setSelectedPayment] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    // Calculate totals
    const subtotal = cartItems
        .reduce((sum, item) => sum + parseFloat(item.price) * item.quantity, 0)
        .toFixed(2);

    const selectedFee = selectedPayment
        ? paymentOptions.find((p) => p.id === selectedPayment)?.fee || 0
        : 0;

    const finalTotal = (parseFloat(subtotal) + parseFloat(selectedFee)).toFixed(2);

    // Authentication & cart validation
    useEffect(() => {
        if (authLoading || cartLoading) return;

        if (!isAuthenticated) {
            toast.error('Please login to proceed to checkout');
            history.push('/login');
            return;
        }

        if (cartItems.length === 0) {
            toast.error('Your cart is empty');
            history.push('/product');
        }
    }, [isAuthenticated, authLoading, cartLoading, history, cartItems]);

    const handlePaymentSelection = (paymentId) => {
        const selectedOption = paymentOptions.find((p) => p.id === paymentId);

        if (selectedOption?.unavailable) {
            toast.error(`${selectedOption.name} is currently unavailable. Please use FPX banking.`);
            return;
        }

        setSelectedPayment(paymentId);
    };

    const handlePlaceOrder = async () => {
        if (!selectedPayment) {
            toast.error('Please select a payment method');
            return;
        }

        // ➡️ Pastikan 'api' tersedia
        if (!api) {
            toast.error('Configuration error. Please refresh.', { id: 'api-error' });
            return;
        }

        setIsProcessing(true);
        const toastId = toast.loading('Processing your order...');

        try {
            const order_id = `ORD-${uuidv4().substring(0, 8)}`;

            const paymentData = {
                amount: parseFloat(finalTotal),
                name: username,
                email: email,
                phone: '0123456789',
                order_id,
                payment_method: selectedPayment,
                items: cartItems,
            };

            // ➡️ Gunakan 'api.post' (Axios) - automatik hantar session kuki
            const response = await api.post('/payment/create', paymentData);

            // Axios sudah throw error untuk status bukan 2xx, jadi kita terus guna response.data
            toast.success('Payment initiated! Redirecting...', { id: toastId });

            // Redirect to payment gateway
            window.location.href = response.data.payment_url;
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to process payment';
            toast.error(errorMsg, { id: toastId });
            setIsProcessing(false);
        }
    };

    if (authLoading || cartLoading) {
        return (
            <div className="checkout-page loading-state">
                <p>Loading your cart...</p>
            </div>
        );
    }
    
    return (
        <div className="checkout-page">
            <h1>Checkout</h1>
            <div className="checkout-container">
                <div className="order-summary">
                    <h2>Order Summary</h2>
                    {cartItems.map((item, index) => (
                        <div key={index} className="checkout-item">
                            <img
                                src={item.image_link || '/placeholder.jpg'}
                                alt={item.name}
                                className="checkout-image"
                                onError={(e) => (e.target.src = '/placeholder.jpg')}
                            />
                            <div className="checkout-details">
                                <h4>{item.name}</h4>
                                <p className="checkout-duration">🕒 {item.selected_duration}</p>
                                <p className="checkout-qty">Quantity: {item.quantity}</p>
                                <p className="checkout-price">Price: RM{parseFloat(item.price).toFixed(2)}</p>
                            </div>
                        </div>
                    ))}
                    <div className="summary-total">
                        <h3>Final Total: RM{finalTotal}</h3>
                    </div>
                </div>

                <div className="checkout-confirmation">
                    <h2>Confirmation</h2>
                    <p className="confirmation-note">
                        📩 A code will be sent to Dashboard after successful checkout.
                    </p>

                    <h3>Select Payment Method</h3>
                    <div className="payment-options">
                        {paymentOptions.map((option) => (
                            <div
                                key={option.id}
                                className={`payment-option ${selectedPayment === option.id ? 'selected' : ''} ${
                                    option.unavailable ? 'unavailable' : ''
                                }`}
                                onClick={() => handlePaymentSelection(option.id)}
                            >
                                <img src={option.logo} alt={option.name} className="payment-logo" />
                                <div>
                                    <span>{option.name}</span>
                                    {option.unavailable && <span className="unavailable-badge">Coming Soon</span>}
                                    <p className="fee-text">Service charge: RM{option.fee.toFixed(2)}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="final-total-bar">
                        <p className="checkout-price2">Subtotal: RM{subtotal}</p>
                        <p className="fee-text">
                            Service Charge {selectedPayment.toUpperCase()}: RM{selectedFee.toFixed(2)}
                        </p>
                        <p className="final-price">Total to Pay: RM{finalTotal}</p>
                    </div>

                    <div className="checkout-actions">
                        <button
                            className="place-order-btn"
                            onClick={handlePlaceOrder}
                            disabled={isProcessing}
                        >
                            {isProcessing ? 'Processing...' : 'Place Order'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;